import React, { Component } from 'react';
import PropTypes from 'prop-types';
import chroma from 'chroma-js';
import { Bert } from 'meteor/themeteorchef:bert';
import {
  assign,
  cloneDeep,
  find,
  findIndex,
  forEach,
  get,
  includes,
  isEmpty,
  isNaN,
  isNil,
  keys,
  map,
  pull,
  sortBy
} from 'lodash';
import { Row, Col, Alert, Button, Form } from 'react-bootstrap';

import * as graphicOps from '../../../api/Graphics/graphic-operations';
import * as layerOps from '../../../api/Layers/layer-operations';
import { GRAPH_STYLE_TYPES } from '../../../api/Graphics/Graphics';

import EditorButtonRow from '../EditorButtonRow';
import GraphicAttributeStep from '../GraphicAttributeStep';
import GraphicLegendStep from '../GraphicLegendStep';

import Validation from '../../containers/Validation';
import GraphicsSelectionContainer from '../../containers/GraphicsSelectionContainer';

import { GRAPH_ELEMENTS, ENTRY_DATA_TYPES } from '../../../modules/graphic-elements';
import loadTableLayer from '../../../modules/load-table-layer';
import handleErrorInClient from '../../../modules/handle-error-in-client';
import methodPromise from '../../../modules/method-promise-wrapper';
import getGraphicProps, { DEFAULT_STYLE } from '../../../modules/get-graphic-props';

class GraphicEditor extends Component {
  state = {
    height: window.innerHeight - 2,
    width: window.innerWidth,
    editionStep: graphicOps.getId(this.props.graphic) ? 1 : 0,
    numericProperties: graphicOps.getNumericProperties(this.props.graphic),
    globalProperties: graphicOps.getGlobalProperties(this.props.graphic),
    namedProperty: graphicOps.getNamedProperty(this.props.graphic),
    graphicType: graphicOps.getType(this.props.graphic),
    selectedElements: graphicOps.getSelectedElements(this.props.graphic),
    attributes: [],
    values: []
  };

  initialState = { ...this.state, editionStep: 0 };

  async componentDidMount() {
    this.updateDimensions();
    window.addEventListener('resize', this.updateDimensions);
    await this.loadAttributes();
  }

  componentDidUpdate(prevProps, prevState) {
    const { graphicType } = this.state;
    const { graphicType: prevGraphicType, editionStep: prevEditionStep } = prevState;
    if (isEmpty(prevGraphicType) && !isEmpty(graphicType) && prevEditionStep === 0) {
      this.updateDimensions();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDimensions);
  }

  loadAttributes = async () => {
    const { layer } = this.props;
    try {
      const { tableBody, tableTitle } = await loadTableLayer(layer);
      this.setState({ attributes: tableTitle, values: tableBody });
      assign(this.initialState, { attributes: tableTitle, values: tableBody });
    } catch (error) {
      Bert.alert(error.message, 'danger');
    }
  };

  updateDimensions = () => {
    let aspectRatio = 1;
    switch (true) {
      case window.innerWidth < 576:
        aspectRatio = 12 / 12;
        break;

      case window.innerWidth >= 576 && window.innerWidth < 768:
        aspectRatio = 8 / 12;
        break;

      case window.innerWidth >= 768:
        aspectRatio = 9 / 12;
        break;

      default:
        break;
    }
    this.setState({
      height: window.innerHeight,
      width: window.innerWidth,
      aspectRatio
    });
  };

  changeEditionStep = stepNumber => this.setState({ editionStep: stepNumber });

  changeGraphicType = newGraphicType => this.setState({ graphicType: newGraphicType });

  resetGraphicState = () => this.setState(this.initialState);

  updateNamedAttribute = attribute => {
    const { namedProperty } = this.state;
    if (namedProperty === attribute) {
      this.setState({ namedProperty: '' });
    } else {
      this.setState({ namedProperty: attribute });
    }
  };

  updateNumericAttributes = attribute => {
    let { numericProperties: newNumericProperties } = this.state;
    if (find(newNumericProperties, ['property', attribute])) {
      const property = find(newNumericProperties, ['property', attribute]);
      pull(newNumericProperties, property);
    } else {
      const { graphicType } = this.state;
      const graphicProperties = GRAPH_ELEMENTS.properties[graphicType];
      const dataNumericType = get(graphicProperties, 'dataNumericType');
      const styleData = {
        ...DEFAULT_STYLE,
        legend: this.generateEmptyLegend(attribute)
      };
      const property = { property: attribute, styleType: GRAPH_STYLE_TYPES.SINGLE, styleData };
      if (
        includes(
          [ENTRY_DATA_TYPES.MULTIPLEVALUES, ENTRY_DATA_TYPES.MULTIPLECOLORSANDVALUES],
          dataNumericType
        )
      ) {
        newNumericProperties.push(property);
      }
      if (
        includes(
          [ENTRY_DATA_TYPES.MULTIPLECOLORS, ENTRY_DATA_TYPES.SINGLECOLORSANDVALUES],
          dataNumericType
        )
      ) {
        newNumericProperties = [property];
      }
    }
    this.setState({ numericProperties: newNumericProperties });
  };

  generateEmptyLegend = numericProperty => {
    const { attributes, values } = this.state;
    const propertyIndex = findIndex(attributes, attribute => attribute === numericProperty);
    const dataNumeric = map(values, valueArray => valueArray[propertyIndex]);
    const sortedDataNumeric = sortBy(dataNumeric, value =>
      isNaN(Number(value)) ? value : Number(value)
    );
    const detectedValues = [];
    const newLegend = [];
    let colorIndex = 0;
    forEach(sortedDataNumeric, value => {
      if (!includes(detectedValues, value)) {
        detectedValues.push(value);
        newLegend.push({ index: colorIndex, value, color: '#fff', count: 0 });
        colorIndex += 1;
      }
      find(newLegend, ['value', value]).count += 1;
      return '';
    });
    return newLegend;
  };

  updateColor = (colorStyle, property) => {
    const { numericProperties } = this.state;
    const newNumericProperties = cloneDeep(numericProperties);
    const numericProperty = find(newNumericProperties, ['property', property]);

    numericProperty.styleData.color = colorStyle.hex;
    numericProperty.styleType = GRAPH_STYLE_TYPES.SINGLE;
    this.setState({ numericProperties: newNumericProperties });
  };

  updateBorderColor = (colorStyle, property) => {
    const { numericProperties } = this.state;
    const newNumericProperties = cloneDeep(numericProperties);
    const numericProperty = find(newNumericProperties, ['property', property]);

    numericProperty.styleData.borderColor = colorStyle.hex;
    numericProperty.styleData.borderAlpha = colorStyle.rgb.a;
    this.setState({ numericProperties: newNumericProperties });
  };

  changeColorScale = (event, property) => {
    const { numericProperties } = this.state;
    const newNumericProperties = cloneDeep(numericProperties);
    const numericProperty = find(newNumericProperties, ['property', property]);
    const { legend } = numericProperty.styleData;
    const newColorRange = event.target.value.trim();
    const colorScale = chroma.scale(newColorRange).colors(legend.length);
    forEach(legend, (element, index) => {
      legend[index].color = colorScale[index];
    });
    numericProperty.styleData.colorRange = newColorRange;
    numericProperty.styleType = GRAPH_STYLE_TYPES.RANGED;
    this.setState({ numericProperties: newNumericProperties });
  };

  changeStyleType = (event, property) => {
    const { numericProperties } = this.state;
    const newNumericProperties = cloneDeep(numericProperties);
    const numericProperty = find(newNumericProperties, ['property', property]);
    numericProperty.styleType = event.target.value.trim();
    if (numericProperty.styleType === GRAPH_STYLE_TYPES.RANGED) {
      const { colorRange, legend } = numericProperty.styleData;
      const colorScale = chroma.scale(colorRange).colors(legend.length);
      forEach(legend, (element, index) => {
        legend[index].color = colorScale[index];
      });
    }
    this.setState({ numericProperties: newNumericProperties });
  };

  updateAlpha = (event, property) => {
    const { numericProperties } = this.state;
    const newNumericProperties = cloneDeep(numericProperties);
    const numericProperty = find(newNumericProperties, ['property', property]);
    numericProperty.styleData.alpha = Number(event.target.value);
    this.setState({ numericProperties: newNumericProperties });
  };

  updateGlobalProperty = event => {
    const { globalProperties } = this.state;
    const newGlobalProperties = cloneDeep(globalProperties);
    const globalProperty = find(newGlobalProperties, ['name', event.target.name]);
    if (!isNil(globalProperty)) {
      globalProperty.value = event.target.value;
    } else {
      newGlobalProperties.push({ name: event.target.name, value: event.target.value });
    }
    this.setState({ globalProperties: newGlobalProperties });
  };

  updateWeight = (event, property) => {
    const { numericProperties } = this.state;
    const newNumericProperties = cloneDeep(numericProperties);
    const numericProperty = find(newNumericProperties, ['property', property]);
    numericProperty.styleData.weight = Number(event.target.value);
    this.setState({ numericProperties: newNumericProperties });
  };

  changeCustomLegend = (index, rgbColor, property) => {
    const { numericProperties } = this.state;
    const newNumericProperties = cloneDeep(numericProperties);
    const numericProperty = find(newNumericProperties, ['property', property]);
    const { legend } = numericProperty.styleData;
    const arrayIndex = findIndex(legend, ['index', index]);
    legend[arrayIndex].color = rgbColor.hex;
    numericProperty.styleType = GRAPH_STYLE_TYPES.CUSTOM;
    this.setState({ numericProperties: newNumericProperties });
  };

  handleSubmit = async form => {
    const { graphic, layer } = this.props;
    const {
      numericProperties,
      globalProperties,
      namedProperty,
      graphicType,
      selectedElements
    } = this.state;

    const existingGraphic = graphic && graphicOps.getId(graphic);
    const methodToCall = existingGraphic ? 'graphics.update' : 'graphics.insert';
    const layerId = layerOps.getId(layer);

    if (isEmpty(numericProperties)) {
      const message = 'You have to chose a numeric property';
      Bert.alert(message, 'danger');
      throw new Error(message);
    }

    const newGraphic = {
      name: form.name.value.trim(),
      layerId: layerOps.getId(layer),
      graphicType,
      numericProperties,
      globalProperties,
      namedProperty,
      selectedElements
    };

    if (existingGraphic) newGraphic._id = existingGraphic;

    try {
      const graphicId = await methodPromise(methodToCall, newGraphic);
      this.redirectToGraphicView(form, existingGraphic, layerId, graphicId);
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  handleCheckChange = event => {
    const { name } = event.target;
    this.setState(prevState => {
      const { selectedElements } = prevState;
      selectedElements[name] = !selectedElements[name];
      return { selectedElements };
    });
  };

  redirectToGraphicView(form, existingGraphic, layerId, graphicId) {
    const { history } = this.props;
    const confirmation = existingGraphic ? 'Graphic updated!' : 'Graphic added!';
    form.reset();
    Bert.alert(confirmation, 'success');
    history.push(`/layers/${layerId}/graphics/${graphicId}`);
  }

  render() {
    const { graphic } = this.props;
    const {
      width,
      height,
      editionStep,
      numericProperties,
      globalProperties,
      namedProperty,
      graphicType,
      attributes,
      values,
      selectedElements
    } = this.state;

    if (isEmpty(graphicType) || editionStep === 0) {
      return (
        <GraphicsSelectionContainer
          handleGraphicType={key => {
            this.changeGraphicType(key);
            this.changeEditionStep(1);
          }}
        />
      );
    }

    const { dataNumericType, dataNamedType, value, humanValue } = GRAPH_ELEMENTS.properties[
      graphicType
    ];
    const graphicToShow = {
      graphicType,
      numericProperties,
      globalProperties,
      namedProperty,
      selectedElements
    };
    const graphicProps = getGraphicProps({
      width,
      height,
      graphic: graphicToShow,
      attributes,
      values,
      aspectRatio: 0.5
    });

    const previewWithProps = React.cloneElement(value, graphicProps);

    const validationProps = {
      rules: {
        name: {
          required: true
        }
      },
      messages: {
        name: {
          required: 'Need a name in here, Seuss.'
        }
      },
      submitHandler: form => {
        this.handleSubmit(form);
      }
    };

    return (
      <Validation {...validationProps}>
        <Form onSubmit={event => event.preventDefault()}>
          <Row>
            <Col xs={12} md={6}>
              <EditorButtonRow
                resetState={this.resetGraphicState}
                editionStep={editionStep}
                changeEditionStep={this.changeEditionStep}
                item="graphic"
              />
              {editionStep === 1 && (
                <GraphicAttributeStep
                  dataNamedType={dataNamedType}
                  attributes={attributes}
                  namedProperty={namedProperty}
                  numericProperties={numericProperties}
                  updateNamedAttribute={this.updateNamedAttribute}
                  updateNumericAttributes={this.updateNumericAttributes}
                  changeEditionStep={this.changeEditionStep}
                />
              )}
              {editionStep === 2 && (
                <GraphicLegendStep
                  graphicType={graphicType}
                  numericProperties={numericProperties}
                  globalProperties={globalProperties}
                  dataNumericType={dataNumericType}
                  changeStyleType={this.changeStyleType}
                  updateColor={this.updateColor}
                  updateAlpha={this.updateAlpha}
                  updateGlobalProperty={this.updateGlobalProperty}
                  changeColorScale={this.changeColorScale}
                  changeCustomLegend={this.changeCustomLegend}
                  updateBorderColor={this.updateBorderColor}
                  updateWeight={this.updateWeight}
                  changeEditionStep={this.changeEditionStep}
                  isMD={width >= 768}
                />
              )}
              {editionStep === 3 && (
                <>
                  <Form.Group>
                    <Form.Label>Name</Form.Label>
                    <Form.Control
                      name="name"
                      defaultValue={graphicOps.getName(graphic)}
                      placeholder="Oh, The Places You'll Go!"
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Elements To Show In Graphic</Form.Label>
                    {map(keys(selectedElements), key => (
                      <Form.Check
                        key={key}
                        name={key}
                        label={key}
                        checked={selectedElements[key]}
                        onChange={this.handleCheckChange}
                        disabled={key === 'Legend'}
                      />
                    ))}
                  </Form.Group>
                  <Button type="submit" variant="success">
                    {isEmpty(graphicOps.getId(graphic)) ? 'Add Graphic' : 'Save Graphic'}
                  </Button>
                </>
              )}
            </Col>
            <Col
              xs={12}
              md={6}
              style={{
                height: graphicProps.height,
                borderLeft: width >= 768 ? '1px solid #ccc' : ''
              }}
            >
              <Alert variant="info">{`Previewing ${humanValue}`}</Alert>
              {previewWithProps}
            </Col>
          </Row>
        </Form>
      </Validation>
    );
  }
}

GraphicEditor.defaultProps = {
  graphic: {
    name: '',
    graphicType: '',
    styleType: GRAPH_STYLE_TYPES.SINGLE
  }
};

GraphicEditor.propTypes = {
  graphic: PropTypes.object,
  layer: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired
};

export default GraphicEditor;
